import { useMemo, useState, useEffect } from "react";
import { GridSortModel, GridPaginationModel } from "@mui/x-data-grid";
import { useQuery } from "@apollo/client";
import { useDebounce } from "../../../../../../hooks/useDebounce";
import { useCompanyPageStore } from "../../../../../../store/companyPageStore";
import {
  COMPANY_INSTALLS_TABLE_QUERY,
  CompanyInstallsPayload,
} from "../../../../../../legacy/queries/Installs";
import { SpogInstall } from "../../../../../../legacy/types/SpogInstall";
import { COMPANY_SEARCH_CLIENT } from "../../../../../../utils/consts";
import {
  DEFAULT_SORT_MODEL,
  DEFAULT_PAGE_SIZE,
  DEFAULT_BACKEND_SORTS,
  DEFAULT_PRODUCT_ID_SORT,
} from "./consts";

const columnFieldToBackendSortField: Record<string, string | undefined> = {
  productName: "product_name",
  vendorName: "product_vendor_name",
  productCategory: "product_category_name",
  productAttributes: "product_attribute_names",
  locations: "locations",
  firstVerified: "first_verified",
  lastVerified: "last_verified",
  intensity: "intensity",
  url: "url",
};

export type SkeletonInstallRow = { hgIdAny: string; isSkeleton: true };
export type DataInstallRow = SpogInstall & { isSkeleton: false };
export type TableInstallRow = DataInstallRow | SkeletonInstallRow;

interface UseInstallsTableProps {
  hgId: string;
  searchTerm: string;
}

export const useInstallsTable = ({
  hgId,
  searchTerm,
}: UseInstallsTableProps) => {
  const installFilters = useCompanyPageStore((state) => state.installFilters);
  const debouncedSearchTerm = useDebounce(searchTerm, 600);
  const [sortModel, setSortModel] = useState<GridSortModel>(DEFAULT_SORT_MODEL);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: DEFAULT_PAGE_SIZE,
  });
  const [lastResultsCount, setLastResultsCount] = useState<number | undefined>(
    0
  );

  const mappedQuerySorts = useMemo(() => {
    if (!sortModel.length) return DEFAULT_BACKEND_SORTS;

    const mapped = sortModel
      .filter((item) => item.field in columnFieldToBackendSortField)
      .map((item) => {
        return {
          field: columnFieldToBackendSortField[item.field],
          direction: item.sort === "asc" ? "ASC" : "DESC",
        };
      });

    return mapped.length > 0
      ? [...mapped, DEFAULT_PRODUCT_ID_SORT]
      : DEFAULT_BACKEND_SORTS;
  }, [sortModel]);

  const { data, loading, error } = useQuery<CompanyInstallsPayload>(
    COMPANY_INSTALLS_TABLE_QUERY,
    {
      variables: {
        hgId,
        page: paginationModel.page,
        per: paginationModel.pageSize,
        sort: mappedQuerySorts,
        countryAlpha2s: installFilters.countryAlpha2s,
        productIds: installFilters.productIds,
        vendorIds: installFilters.vendorIds,
        productCategoryIds: installFilters.productCategoryIds,
        productAttributeIds: installFilters.productAttributeIds,
        includeCorporateDescendants: installFilters.includeCorporateDescendants,
        fuzzySearch: debouncedSearchTerm,
      },
      context: {
        clientName: COMPANY_SEARCH_CLIENT,
      },
      skip: !hgId,
    }
  );

  const hasResults = Boolean(data?.install?.data?.length);
  const resultsCount = data?.install?.count;

  // Preserve resultsCount during loading to maintain pagination
  const currentResultsCount = useMemo(() => {
    if (resultsCount !== undefined) {
      setLastResultsCount(resultsCount);
      return resultsCount;
    }
    return loading ? lastResultsCount : undefined;
  }, [resultsCount, loading, lastResultsCount]);

  // Reset to first page when search term changes
  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  }, [debouncedSearchTerm]);

  // Reset to first page when sort changes
  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
  }, [sortModel]);

  const handleSortModelChange = (model: GridSortModel) => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 }
    );
    setSortModel(model);
  };

  const rows: TableInstallRow[] = useMemo(() => {
    if (loading) {
      return Array.from({ length: paginationModel.pageSize }).map(
        (_, index) =>
          ({
            hgIdAny: `skeleton-${index}-${paginationModel.page}`,
            isSkeleton: true,
          }) as SkeletonInstallRow
      );
    }
    return (data?.install?.data?.map(
      (install: SpogInstall) =>
        ({
          ...install,
          isSkeleton: false,
        }) as DataInstallRow
    ) || []) as TableInstallRow[];
  }, [data, loading, paginationModel.pageSize, paginationModel.page]);

  return {
    debouncedSearchTerm,
    sortModel,
    setSortModel: handleSortModelChange,
    paginationModel,
    setPaginationModel,
    loading,
    error,
    rows,
    resultsCount: currentResultsCount,
    hasResults,
  };
};
